// src/applications/MainComponent.jsx
import {
  useState,
  useEffect,
  useMemo,
  useReducer,
  useCallback,
} from "react"

import { Box } from "@mui/material"

import "leaflet/dist/leaflet.css"
import { MeteoTrackerPreset } from "@geoflow/benchmap"

import "leaflet-marker-rotation"
import "leaflet-polylinedecorator" // leaflet-marker-rotation und leaflet-polylinedecorator verändern beide rotationAngle!!!!! https://github.com/bbecquet/Leaflet.PolylineDecorator/issues/94
import { NmScale } from "@marfle/react-leaflet-nmscale"

import DrawerList from "@/features/navigation/DrawerList.jsx"
import AppHeader from "@/features/navigation/AppHeader.jsx"
import AppDrawer from "@/features/navigation/AppDrawer.jsx"
import { drawerWidth, appBarHeight } from "@/config/layout"
import { publishDrawerOpen } from "@/features/layout/layoutBounds.jsx"

import MyMapEvent from "@/features/map/MyMapEvent.jsx"
import MapSetup from "@/features/map/MapSetup.jsx"
import MapUiSlot from "@/features/map/MapUiSlot.jsx"
import MapBaseLayers from "@/features/map/MapBaseLayers.jsx"
import MapControls from "@/features/map/MapControls.jsx"
import { useMapViewState } from "@/features/map/useMapViewState.jsx"
import RecenterAutomaticallyMap from "@/features/map/RecenterAutomaticallyMap.jsx"
import useMapRecenter, {
  useRouteRecenterResolver,
} from "@/features/map/useMapRecenter.jsx"

import MapAttribution from "@/features/map/MapAttribution.jsx"

import GisLayers from "@/features/gis/GisLayers.jsx"
import { GisInteractivityProvider } from "@/features/gis/GisInteractivityContext.jsx"
import { useGisData } from "@/features/gis/hooks/useGisData.jsx"

import VoyagePlannerController from "@/features/voyagePlanner/VoyagePlannerController.jsx"
import TablePanel from "@/features/table/TablePanel.jsx"
import PathfinderController from "@/features/voyagePlanner/PathfinderController.jsx"

import RouteController from "@/features/route/RouteController.jsx"
import VesselController from "@/features/vessel/VesselController.jsx"
import VoyagePlannerPanel from "@/features/voyagePlanner/VoyagePlannerPanel.jsx"
import RnwPanel from "@/features/rnwAnalyser/RnwPanel.jsx"
import VesselPanel from "@/features/vessel/VesselPanel.jsx"
import ActiveInfoCard from "@/features/map/ActiveInfoCard.jsx"
import NotificationBell from "@/features/notifications/NotificationBell.jsx"
import GisPanel from "@/features/gis/GisPanel.jsx"
import MapPanel from "@/features/map/MapPanel.jsx"
import WeatherPanel from "@/features/weatherlayers/WeatherPanel.jsx"
import WeatherAvailabilityNotice from "@/features/weatherlayers/WeatherAvailabilityNotice.jsx"
import GlobalTimeSliderContainer from "@/features/timeslider/GlobalTimeSliderContainer.jsx"
import { useTimeSliderVisibility } from "@/features/timeslider/hooks/useTimeSliderVisibility.jsx"

import {
  panelsReducer,
  initialPanelsState,
  openPanel,
} from "../reducers/panelsReducer.jsx"


import { useReportedRoutes } from "../hooks/useReportedRoutes.jsx"
import useRouteImportFlow from "../hooks/useRouteImportFlow.jsx"
import { useAccessContext } from "../contexts/AccessContext.jsx"
import { useTimeSliderState } from "../hooks/useTimeSliderState.jsx"
import { clipRoutesToTimestamp, appendLastPositionsToRoutes } from "../utils/vesselHelper.jsx"
import {
  useWindowStart,
  useSliderRightEdge,
} from "../hooks/useTimeWindow.jsx"
import {
  usePlanRoutes,
  useFleetPlanLayer,
  useImportSelectionBridge,
  useRouteRecalc,
  useRecalcCompletion,
} from "@/features/route/hooks"
import useNotifications from "@/features/notifications/useNotifications.jsx"
import { WeatherRunNotifier } from "@/features/weatherRun"
import { useVesselLastPositions, useVesselsLoader } from "@/features/vessel/hooks"
import { useReportedChangeRefresh } from "@/features/changeFeed"
import useSelectionRevealsLayers from "@/features/routeTree/hooks/useSelectionRevealsLayers.jsx"
import useFleetRouteAutoSelect from "@/features/routeTree/hooks/useFleetRouteAutoSelect.jsx"
import useTreeSelection, {
  NO_VOYAGE_DRILL_ID,
} from "@/features/routeTree/hooks/useTreeSelection.jsx"
import useInfoCard from "@/features/map/useInfoCard.jsx"
import useActiveRouteSummary from "@/features/routeTree/hooks/useActiveRouteSummary.jsx"
import usePlannerDrafts from "@/features/voyagePlanner/hooks/usePlannerDrafts.jsx"
import { usePlannerFormState } from "@/features/voyagePlanner/hooks/usePlannerFormState.jsx"
import useDraftInvalidation from "@/features/voyagePlanner/hooks/useDraftInvalidation.jsx"
import usePlanVoyageForVessel from "@/features/voyagePlanner/hooks/usePlanVoyageForVessel.jsx"
import { useDraftRoutes } from "@/features/route/state/RouteStore.jsx"

import { useWeatherLayers } from "@/features/weatherlayers/hooks/useWeatherLayers.jsx"
import { useWeatherTimeBridge } from "@/features/weatherlayers/hooks/useWeatherTimeBridge.jsx"
import { useVesselViewState } from "@/features/vessel/hooks/useVesselViewState.jsx"
import { usePathfinderState } from "@/features/voyagePlanner/hooks/usePathfinderState.jsx"
import { useUiToggles } from "@/features/navigation/useUiToggles.jsx"
import {
  defaultSpeedProfileData,
  defaultPlannerVesselSettings,
} from "@/config/defaults"

const initialZoom = 3
// maxZoom explizit: benchmaps Default 9 würde den Zoom deckeln; zoomLevel kann fraktional sein
const MAP_VIEW = {
  initialCenter: [0, 0],
  initialZoom,
  minZoom: 3,
  maxZoom: 19,
  zoomSnap: 0.25,
  zoomDelta: 1,
  // benchmap-Default 400 ist auf dem Mac (Delta ÷ 3·dpr) sehr träge
  wheelPxPerZoomLevel: 60,
}
const MAP_STYLE = { height: "100vh", width: "100%" }

export default function MainComponent({ logout }) {
  const [openPanels, dispatch] = useReducer(panelsReducer, initialPanelsState)

  const { accessContext, setAccessContext } = useAccessContext()
  const canSeeFleetOnline =
    accessContext?.uiCapabilities?.canSeeFleetOnline === true
  const canSeeRnwAnalyser =
    accessContext?.uiCapabilities?.canSeeRnwAnalyser === true
  const canSeeErklaerbaer =
    accessContext?.uiCapabilities?.canSeeErklaerbaer === true

  const [openDrawer, setOpenDrawer] = useState(false)
  useEffect(() => publishDrawerOpen(openDrawer), [openDrawer])
  // Wert wird nie gelesen; Setter hängt noch tief in der Vessel-Kette
  // (VesselIcon, PathfinderController) — Rückbau separat.
  const [, setOpenVesselList] = useState(false)
  const [openGeolocationList, setOpenGeolocationList] = useState(false)
  const [openRouteTable, setOpenRouteTable] = useState(false)

  const {
    zoomLevel,
    setZoomLevel,
    dragged,
    setDragged,
    resized,
    setResized,
    mapBounds,
    setMapBounds,
    map,
    setMap,
  } = useMapViewState({ initialZoom })


  const { switchVesselFiltersActive, setSwitchVesselFiltersActive } =
    useVesselViewState()

  // Vessel master data for tree, pickers, spec views and the CII request.
  const { loading: vesselsLoading, error: vesselsError } = useVesselsLoader({
    accessContext,
    setAccessContext,
    refreshSignal: openPanels.vessel || openPanels.voyagePlanner,
  })

  const {
    switchVoyagePlannerActive,
    setSwitchVoyagePlannerActive,
    switchRnwAnalyserActive,
    setSwitchRnwAnalyserActive,
    switchGisLayersActive,
    setSwitchGisLayersActive,
    switchMapSettingsActive,
    setSwitchMapSettingsActive,
    switchManualPathing,
    plannerRoutingMode,
    setPlannerRoutingMode,
    toogleButtonsOnOff,
    setToogleButtonsOnOff,
    handleToogleButtonsOnOff,
  } = useUiToggles()

  const {
    pathfinderResponse,
    setPathfinderResponse,
    erklaerbaerEnabled,
    setErklaerbaerEnabled,
    erklaerbaerId,
    setErklaerbaerId,
    etaError,
    setEtaError,
    voyageRefreshSignal,
    setVoyageRefreshSignal,
  } = usePathfinderState()

  const {
    controller: weatherController,
    geoflowSession,
    hasWeatherTimeSlider,
    weatherMaxTimestamp,
  } = useWeatherLayers()

  const draftRoutes = useDraftRoutes()
  const hasDraftRoute = draftRoutes.length > 0

  const {
    draftPlannerVesselSettings,
    setDraftPlannerVesselSettings,
    voyagePlannerPoints,
    setVoyagePlannerPoints,
    plannerDraftSlot,
    setPlannerDraftSlot,
    voyageActive,
    setVoyageActive,
    startInput,
    setStartInput,
    finishInput,
    setFinishInput,
    startEndpoint,
    setStartEndpoint,
    finishEndpoint,
    setFinishEndpoint,
    startError,
    setStartError,
    finishError,
    setFinishError,
    calculatorDepartureTime,
    setCalculatorDepartureTime,
    calculatorBasespeed,
    setCalculatorBasespeed,
    profileOptions,
    setProfileOptions,
    selectedProfile,
    setSelectedProfile,
    speedProfileData,
    setSpeedProfileData,
    consumptionProfileData,
    setConsumptionProfileData,
    plannerVesselSettings,
    setPlannerVesselSettings,
    etaResponse,
    setEtaResponse,
    routeConsumption,
    setRouteConsumption,
    applyRnwSafety,
    setApplyRnwSafety,
    applyEca,
    setApplyEca,
    avoidHra,
    avoidJwla,
    avoidCaa,
    setAvoidHra,
    setAvoidJwla,
    setAvoidCaa,
    bunkerPrices,
    setBunkerPrices,
    currentDraft,
    setCurrentDraft,
  } = usePlannerFormState()

  const {
    cwPortGeojson,
    setCwPortGeojson,
    rnwLegs,
    setRnwLegs,
    cwRegionGeojson,
    setCwRegionGeojson,
    cwRestrictionGeojson,
    setCwRestrictionGeojson,
    cwPilotStationGeojson,
    setCwPilotStationGeojson,
    cwViaAreasGeojson,
    setCwViaAreasGeojson,
    cwLegsToViaAreasGeojson,
    setCwLegsToViaAreasGeojson,
  } = useGisData()

  const { lastPositionByVesselId, lastPositions } = useVesselLastPositions({
    accessContext,
    setAccessContext,
  })
  const {
    centerLatLonMap,
    centerZoom,
    recenterToPoint,
    recenterToVesselLastPosition,
    recenterToRoute,
    routeRecenterRequest,
  } = useMapRecenter({ lastPositionByVesselId, user: accessContext?.payload?.name })
  const [importSelectionHint, setImportSelectionHint] = useState(null)
  const {
    unreadCount: notificationUnreadCount,
    notifications,
    anchorEl: notificationsAnchorEl,
    addNotification,
    openMenu: openNotificationsMenu,
    closeMenu: closeNotificationsMenu,
  } = useNotifications(20)
  const {
    treeState,
    treeDispatch,
    drillPath,
    setDrillPath,
    drillRequest,
    requestDrillToVessel,
    clearDrillRequest,
    treeData,
    markedVesselNames,
    setMarkedVesselNames,
  } = useTreeSelection({
    accessContext,
    setAccessContext,
  })

  const treeSelectedRouteIds = treeState.ui.selectedRouteIds

  const {
    vesselPlanRoutes,
    refetchPlanRoutes,
    refreshNonce: planRefreshNonce,
  } = usePlanRoutes({
    selectedRouteIds: treeSelectedRouteIds,
    accessContext,
    setAccessContext,
  })

  const {
    switchFleetPlan,
    fetchingFleetPlan,
    fleetPlanSummary,
    handleToggleFleetPlan,
    displayedPlanRoutes,
    vesselLabelMap,
    planLayerVisible,
  } = useFleetPlanLayer({
    enabled: openPanels.vessel,
    vesselPlanRoutes,
    markedVesselNames,
    setSwitchVesselFiltersActive,
    accessContext,
    setAccessContext,
    refreshNonce: planRefreshNonce,
  })

  const { fromPicker, reportedRefreshTick, triggerReportedRefresh } =
    useWindowStart({
      planRoutes: displayedPlanRoutes,
      showHistory: markedVesselNames.length > 0,
    })

  const { activeRouteId, routeSummariesById, routeTimestampsById } =
    useActiveRouteSummary(treeSelectedRouteIds)

  const openVesselPanelFromMap = useCallback(
    (vesselName, vesselId, voyageId = undefined) => {
      dispatch(openPanel("vessel"))
      setSwitchVesselFiltersActive(true)
      if (!vesselName) return
      if (vesselId != null) {
        setMarkedVesselNames([{ vesselName, vesselId }])
      }
      // undefined = caller has no voyage context (map icon); null = planner has
      // a vessel but no voyage → drill into the "New voyage" placeholder.
      const drillVoyageId =
        voyageId === undefined
          ? null
          : (voyageId ?? NO_VOYAGE_DRILL_ID)
      requestDrillToVessel(vesselName, {
        recenter: false,
        voyageId: drillVoyageId,
      })
    },
    [
      dispatch,
      setSwitchVesselFiltersActive,
      setMarkedVesselNames,
      requestDrillToVessel,
    ],
  )

  useSelectionRevealsLayers({
    selectedRouteIds: treeSelectedRouteIds,
    setSwitchVesselFiltersActive,
  })
  useFleetRouteAutoSelect({
    switchFleetPlan,
    switchVesselFiltersActive,
    markedVesselId: markedVesselNames[0]?.vesselId,
    routeByVesselId: fleetPlanSummary.routeByVesselId,
    treeDispatch,
  })
  const {
    activeCard,
    closeCard,
    setSelectedVessel,
    setSelectedVesselAnchor,
    selectedVessel,
    selectedVesselAnchor,
    openWaypointCard,
    openAlertCard,
  } = useInfoCard({ hasCandidateRoute: hasDraftRoute, markedVesselNames, map })

  const { planVoyageForVessel, newVoyageVesselId, endNewVoyagePlan } = usePlanVoyageForVessel({ dispatch, setMarkedVesselNames, plannerOpen: openPanels.voyagePlanner, setVoyageActive, setSwitchVoyagePlannerActive, setToogleButtonsOnOff })

  useDraftInvalidation({
    currentVesselId: Number(markedVesselNames?.[0]?.vesselId),
    plannerVesselSettings,
    speedProfileData,
    consumptionProfileData,
    applyRnwSafety,
    cwViaAreasGeojson,
    keepManualWaypoints: switchManualPathing,
    // Only the draft line is invalidated on an input change; the last result
    // (ETA/consumption/pathfinderResponse) stays visible until a recompute so
    // it doesn't vanish when the user edits an input to fix a warning.
    onAfterClear: () => {},
  })
  const { handleImportSuccess } = useImportSelectionBridge({
    addNotification,
    setSwitchVesselFiltersActive,
    setImportSelectionHint,
    setMarkedVesselNames,
    recenterToPoint,
    refetchPlanRoutes,
  })

  const onRecalculated = useRecalcCompletion({
    treeDispatch,
    refetchPlanRoutes,
    recenterToRoute,
    loadRoutesForVoyage: treeData?.loadRoutesForVoyage,
    addNotification,
  })
  const onRecalcError = useCallback(
    (message) => addNotification({ message: `Recalc failed: ${message}`, type: "error" }),
    [addNotification],
  )
  const { recalculateRoute, recalculatingRouteId, recalcErrorByRouteId, clearRecalcError } =
    useRouteRecalc({ onRecalculated, onError: onRecalcError })

  // {/* ---route import flow -------------------------------------------------------------- */}
  const {
    importDraft,
    importDialogOpen,
    importSaving,
    handleImportDraft,
    handleImportCancel,
    handleImportConfirm,
  } = useRouteImportFlow(
    triggerReportedRefresh,
    (err) => alert("Import failed: " + err?.message),
    (payload) => {
      handleImportSuccess(payload)
      setVoyageRefreshSignal((n) => n + 1)
    },
    openPanels.vessel,
    recenterToRoute,
  )

  const {
    treeDraftRoutes,
    mapDraftRoutes,
    pathfinderMapRoutes,
    selectedDraftRouteIds,
    toggleDraftSelected,
    handleDiscardDraft,
    discardDraftsForVoyage,
    handleRenameDraft,
    handleSaveDraft,
  } = usePlannerDrafts({
    treeSelectedRouteIds,
    treeDispatch,
    addNotification,
    accessContext,
    setAccessContext,
    onPersistedRouteSaved: handleImportSuccess,
    onVoyageCreated: () => {
      setVoyageRefreshSignal((n) => n + 1)
      endNewVoyagePlan()
    },
    selectedVoyageIds: treeState.ui.selectedVoyageIds,
    switchVoyagePlannerActive,
    importPreviewOpen: importDialogOpen,
    plannerDraftSlot,
    plannerPanelOpen: openPanels.voyagePlanner,
  })

  // {/* ---reported routes: fetch + analyse + filter -------------------------------------------------------------- */}
  const {
    selectedVesselId,
    switchReported,
    fetchingReported,
    loadingReportedAnalysis,
    reportedWindowDays,
    vesselReportedRoutes,
  } = useReportedRoutes({
    fromPicker,
    markedVesselNames,
    treeSelectedRouteIds,
    reportedRefreshTick,
  })

  useReportedChangeRefresh({ selectedVesselId, switchReported, onChanged: triggerReportedRefresh })

  const shouldDisplayTimeSlider = useTimeSliderVisibility({
    vesselReportedRoutes,
    switchReported,
    switchPlan: planLayerVisible,
    switchFleetPlan,
    switchVoyagePlannerActive,
    hasDraftRoute: mapDraftRoutes.length > 0,
    hasWeatherTimeSlider,
  })

  useEffect(() => {
    const vessels = (lastPositions ?? []).map((p) => ({
      vesselName: p.vesselName,
      vesselId: p.vesselId,
    }))
    setProfileOptions([{ vesselName: "Default", vesselId: null }, ...vessels])
  }, [lastPositions])

  // Spans all displayed plan routes (incl. the fleet-plan overview) plus the
  // visible voyage-planner drafts, so every drawn route fits inside the slider.
  const toPicker = useSliderRightEdge({
    weatherMaxTimestamp,
    planRoutes: displayedPlanRoutes,
    reportedRoutes: vesselReportedRoutes,
    draftRoutes: mapDraftRoutes,
    weatherActive: hasWeatherTimeSlider,
  })

  const { sliderTicks, sliderPosition, setSliderPosition, selectedTimestamp } =
    useTimeSliderState({ fromPicker, toPicker })

  // Für den Preset: blendet Wetter aus, wenn der Slider vor dem ersten verfügbaren Timestep steht
  const {
    presetController: presetWeatherController,
    timeOutOfRange: weatherTimeOutOfRange,
  } = useWeatherTimeBridge({
    controller: weatherController,
    selectedTimestamp,
  })

  useRouteRecenterResolver({
    routeRecenterRequest,
    selectedPlanRoutes: vesselPlanRoutes,
    draftRoutes: mapDraftRoutes,
    sliderPosition,
    recenterToPoint,
  })

  // Clip the reported track to the slider's right edge so it does not extend
  // past the displayed route's end.
  const toPickerMs = toPicker.valueOf()
  const clippedReportedRoutes = useMemo(
    () =>
      clipRoutesToTimestamp(
        appendLastPositionsToRoutes(vesselReportedRoutes, lastPositions),
        toPickerMs,
      ),
    [vesselReportedRoutes, lastPositions, toPickerMs],
  )

  const handleDrawerOpen = () => {
    setOpenDrawer(true)
  }
  const handleDrawerClose = () => {
    setOpenDrawer(false)
    setOpenVesselList(false)
    setOpenGeolocationList(false)
  }

  const drawer = (
    toogleButtonsOnOff,
    setToogleButtonsOnOff,
    handleToogleButtonsOnOff,
  ) => {
    return (
      <Box>
        {toogleButtonsOnOff === null ? null : (
          <DrawerList
            openPanels={openPanels}
            dispatch={dispatch}
            setToogleButtonsOnOff={setToogleButtonsOnOff}
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer}
            drawerWidth={drawerWidth}
            switchVesselFiltersActive={switchVesselFiltersActive}
            setSwitchVesselFiltersActive={setSwitchVesselFiltersActive}
            openVoyagePlanner={openPanels.voyagePlanner}
            switchVoyagePlannerActive={switchVoyagePlannerActive}
            setSwitchVoyagePlannerActive={setSwitchVoyagePlannerActive}
            switchRnwAnalyserActive={switchRnwAnalyserActive}
            setSwitchRnwAnalyserActive={setSwitchRnwAnalyserActive}
            switchGisLayersActive={switchGisLayersActive}
            setSwitchGisLayersActive={setSwitchGisLayersActive}
            openGeolocationList={openGeolocationList}
            setOpenGeolocationList={setOpenGeolocationList}
            logout={logout}
            appBarHeight={appBarHeight}
            voyageActive={voyageActive}
            setVoyageActive={setVoyageActive}
            switchMapSettingsActive={switchMapSettingsActive}
            setSwitchMapSettingsActive={setSwitchMapSettingsActive}
            switchWeatherForecastActive={weatherController.weatherVisible}
            setSwitchWeatherForecastActive={weatherController.setWeatherVisible}
            canSeeRnwAnalyser={canSeeRnwAnalyser}
          />
        )}
      </Box>
    )
  }

  const MemoizedRouteController = useMemo(
    () => (
      // <Profiler id="RouteController" onRender={onRenderCallback}>
      <RouteController
        openPanels={openPanels}
        mapBounds={mapBounds}
        vesselReportedRoutes={clippedReportedRoutes}
        vesselPlanRoutes={displayedPlanRoutes}
        switchReported={switchReported}
        switchPlan={planLayerVisible}
        resized={resized}
        speedProfileData={speedProfileData}
        consumptionProfileData={consumptionProfileData}
        setRouteConsumption={setRouteConsumption}
        setPathfinderResponse={setPathfinderResponse}
        setEtaError={setEtaError}
        switchVesselFiltersActive={switchVesselFiltersActive}
        openWaypointCard={openWaypointCard}
        openAlertCard={openAlertCard}
        calculatorDepartureTime={calculatorDepartureTime}
        calculatorBasespeed={calculatorBasespeed}
        setEtaResponse={setEtaResponse}
        plannerVesselSettings={plannerVesselSettings}
        cwViaAreasGeojson={cwViaAreasGeojson}
        applyRnwSafety={applyRnwSafety}
        applyEca={applyEca}
        avoidHra={avoidHra}
        avoidJwla={avoidJwla}
        avoidCaa={avoidCaa}
      />
    ),
    // </Profiler>
    [
      switchVesselFiltersActive,
      openPanels,
      clippedReportedRoutes,
      displayedPlanRoutes,
      switchReported,
      planLayerVisible,
      mapBounds,
      resized,
      speedProfileData,
      consumptionProfileData,
      calculatorDepartureTime,
      calculatorBasespeed,
      plannerVesselSettings,
      cwViaAreasGeojson,
      applyRnwSafety,
      applyEca,
      avoidHra,
      avoidJwla,
      avoidCaa,
    ],
  )

  const MemoizedVesselController = useMemo(
    () => (
      <VesselController
        vesselReportedRoutes={clippedReportedRoutes}
        vesselPlanRoutes={displayedPlanRoutes}
        vesselLabelMap={vesselLabelMap}
        markedVesselNames={markedVesselNames}
        lastPositions={lastPositions}
        switchReported={switchReported}
        switchPlan={planLayerVisible}
        recenterToPoint={recenterToPoint}
        setMarkedVesselNames={setMarkedVesselNames}
        setOpenDrawer={setOpenDrawer}
        setOpenVesselList={setOpenVesselList}
        setSelectedVessel={setSelectedVessel}
        setSelectedVesselAnchor={setSelectedVesselAnchor}
        resized={resized}
        sliderTicks={sliderTicks}
        sliderPosition={sliderPosition}
        setVoyageActive={setVoyageActive}
        switchVoyagePlannerActive={switchVoyagePlannerActive}
        onMapVesselClick={openVesselPanelFromMap}
      />
    ),
    [
      clippedReportedRoutes,
      displayedPlanRoutes,
      vesselLabelMap,
      lastPositions,
      switchReported,
      planLayerVisible,
      resized,
      sliderPosition,
      switchVoyagePlannerActive,
      sliderTicks,
      openVesselPanelFromMap,
      recenterToPoint,
    ],
  )

  const userInitial = (accessContext?.payload?.name ?? "")
    .trim()
    .charAt(0)
    .toUpperCase()

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader
        openDrawer={openDrawer}
        onDrawerOpen={handleDrawerOpen}
        canSeeFleetOnline={canSeeFleetOnline}
        userInitial={userInitial}
      >
        <NotificationBell
          unreadCount={notificationUnreadCount}
          notifications={notifications}
          anchorEl={notificationsAnchorEl}
          onOpen={openNotificationsMenu}
          onClose={closeNotificationsMenu}
        />
        <WeatherRunNotifier addNotification={addNotification} />
      </AppHeader>
      <AppDrawer open={openDrawer} onClose={handleDrawerClose}>
        {drawer(
          toogleButtonsOnOff,
          setToogleButtonsOnOff,
          handleToogleButtonsOnOff,
        )}
      </AppDrawer>
      <MeteoTrackerPreset
        controller={presetWeatherController}
        languageKey="en"
        showScenePanel={false}
        showTimeControl={false}
        view={MAP_VIEW}
        style={MAP_STYLE}
      >
        <MapUiSlot>
        <MapSetup onMap={setMap} />
        <MyMapEvent
          setZoomLevel={setZoomLevel}
          setDragged={setDragged}
          setResized={setResized}
          setMapBounds={setMapBounds}
        />
        {!toogleButtonsOnOff.includes("NautiScale") ? null : (
          <div>
            <NmScale position="bottomright" />
          </div>
        )}
        <MapBaseLayers />
        <MapControls toggleFlags={toogleButtonsOnOff} />
        <GisInteractivityProvider
          suppressed={
            openPanels.voyagePlanner && voyageActive && switchVoyagePlannerActive
          }
        >
          <GisLayers
            toggleFlags={toogleButtonsOnOff}
            switchGisLayersActive={switchGisLayersActive}
            zoomLevel={zoomLevel}
            dragged={dragged}
            resized={resized}
            setVoyageActive={setVoyageActive}
            cwPortGeojson={cwPortGeojson}
            setCwPortGeojson={setCwPortGeojson}
            cwRegionGeojson={cwRegionGeojson}
            setCwRegionGeojson={setCwRegionGeojson}
            cwRestrictionGeojson={cwRestrictionGeojson}
            setCwRestrictionGeojson={setCwRestrictionGeojson}
            cwViaAreasGeojson={cwViaAreasGeojson}
            setCwViaAreasGeojson={setCwViaAreasGeojson}
            cwPilotStationGeojson={cwPilotStationGeojson}
            setCwPilotStationGeojson={setCwPilotStationGeojson}
          />
        </GisInteractivityProvider>
        <RecenterAutomaticallyMap centerLatLonMap={centerLatLonMap} zoom={centerZoom} />
        {MemoizedRouteController}
        {MemoizedVesselController}
        <ActiveInfoCard activeCard={activeCard} onClose={closeCard} />
        <VoyagePlannerController
          openPanels={openPanels}
          voyagePlannerPoints={voyagePlannerPoints}
          setVoyagePlannerPoints={setVoyagePlannerPoints}
          plannerDraftSlot={plannerDraftSlot}
          startEndpoint={startEndpoint}
          setStartEndpoint={setStartEndpoint}
          finishEndpoint={finishEndpoint}
          setFinishEndpoint={setFinishEndpoint}
          setStartInput={setStartInput}
          setFinishInput={setFinishInput}
          voyageActive={voyageActive}
          setVoyageActive={setVoyageActive}
          toogleButtonsOnOff={toogleButtonsOnOff}
          switchVoyagePlannerActive={switchVoyagePlannerActive}
          switchManualPathing={switchManualPathing}
          setOpenRouteTable={setOpenRouteTable}
          openWaypointCard={openWaypointCard}
        />
        <TablePanel
          vesselPlanRoutes={vesselPlanRoutes}
          selectedDraftRoutes={mapDraftRoutes}
          activeRouteId={activeRouteId}
          routeSummariesById={routeSummariesById}
          routeTimestampsById={routeTimestampsById}
          setVoyageActive={setVoyageActive}
          vesselPanelOpen={openPanels.vessel}
          otherPanelOpen={Object.entries(openPanels).some(
            ([key, open]) => open && key !== "vessel",
          )}
          openRouteTable={openRouteTable}
          setOpenRouteTable={setOpenRouteTable}
          routeConsumption={routeConsumption}
        />
        <PathfinderController
          mapBounds={mapBounds}
          sliderPosition={sliderPosition}
          markedVesselNames={markedVesselNames}
          recenterToPoint={recenterToPoint}
          setMarkedVesselNames={setMarkedVesselNames}
          setOpenDrawer={setOpenDrawer}
          setOpenVesselList={setOpenVesselList}
          map={map}
          calculatorDepartureTime={calculatorDepartureTime}
          calculatorBasespeed={calculatorBasespeed}
          setEtaResponse={setEtaResponse}
          speedProfileData={speedProfileData}
          consumptionProfileData={consumptionProfileData}
          setRouteConsumption={setRouteConsumption}
          switchVoyagePlannerActive={switchVoyagePlannerActive}
          switchVesselFiltersActive={switchVesselFiltersActive}
          setPathfinderResponse={setPathfinderResponse}
          setEtaError={setEtaError}
          setSelectedVessel={setSelectedVessel}
          setSelectedVesselAnchor={setSelectedVesselAnchor}
          draftRoutes={pathfinderMapRoutes}
          plannerDraftRouteId={plannerDraftSlot.routeId}
          openWaypointCard={openWaypointCard}
          openAlertCard={openAlertCard}
          plannerVesselSettings={plannerVesselSettings}
          cwViaAreasGeojson={cwViaAreasGeojson}
          applyRnwSafety={applyRnwSafety}
          applyEca={applyEca}
          avoidHra={avoidHra}
          avoidJwla={avoidJwla}
          avoidCaa={avoidCaa}
        />
        <VesselPanel
          isOpen={openPanels.vessel}
          vesselsLoading={vesselsLoading}
          vesselsError={vesselsError}
          dispatch={dispatch}
          routeTableOpen={openRouteTable}
          markedVesselNames={markedVesselNames}
          setMarkedVesselNames={setMarkedVesselNames}
          vesselReportedRoutes={vesselReportedRoutes}
          fetchingReported={fetchingReported || loadingReportedAnalysis}
          switchFleetPlan={switchFleetPlan}
          onToggleFleetPlan={handleToggleFleetPlan}
          fetchingFleetPlan={fetchingFleetPlan}
          fleetPlanSummary={fleetPlanSummary}
          reportedWindowDays={reportedWindowDays}
          setVoyageActive={setVoyageActive}
          switchVesselFiltersActive={switchVesselFiltersActive}
          setSwitchVesselFiltersActive={setSwitchVesselFiltersActive}
          treeState={treeState}
          treeDispatch={treeDispatch}
          drillPath={drillPath}
          setDrillPath={setDrillPath}
          drillRequest={drillRequest}
          clearDrillRequest={clearDrillRequest}
          treeData={treeData}
          draftRoutes={treeDraftRoutes}
          selectedDraftRouteIds={selectedDraftRouteIds}
          onToggleDraftSelected={toggleDraftSelected}
          onDiscardDraft={handleDiscardDraft}
          onRenameDraft={handleRenameDraft}
          onSaveDraft={handleSaveDraft}
          recenterToVesselLastPosition={recenterToVesselLastPosition}
          recenterToRoute={recenterToRoute}
          onImportDraft={handleImportDraft}
          onPlanVoyage={planVoyageForVessel}
          importDraft={importDraft}
          importSaving={importSaving}
          onImportCancel={handleImportCancel}
          onImportConfirm={handleImportConfirm}
          onImportSelectionHint={importSelectionHint}
          onCardClose={closeCard}
          onRouteDeleted={({ routeId, routeName }) => {
            refetchPlanRoutes()
            addNotification({
              message: `Deleted route: ${routeName || `Route ${routeId ?? ""}`}`,
              type: "success",
            })
          }}
          onVoyageDeleted={({ voyageId, voyageName }) => {
            discardDraftsForVoyage(voyageId)
            setVoyageRefreshSignal((n) => n + 1)
            refetchPlanRoutes()
            addNotification({
              message: `Deleted voyage: ${voyageName || `Voyage ${voyageId ?? ""}`}`,
              type: "success",
            })
          }}
          onRouteStateChanged={refetchPlanRoutes}
          onRecalculateRoute={recalculateRoute}
          recalculatingRouteId={recalculatingRouteId}
          recalcErrorByRouteId={recalcErrorByRouteId}
          onClearRecalcError={clearRecalcError}
          onOpenRouteTable={() => setOpenRouteTable(true)}
        />
        <WeatherPanel
          isOpen={openPanels.weather}
          dispatch={dispatch}
          setVoyageActive={setVoyageActive}
          controller={weatherController}
          geoflowSession={geoflowSession}
        />
        <VoyagePlannerPanel
          isOpen={openPanels.voyagePlanner}
          dispatch={dispatch}
          voyagePlannerPoints={voyagePlannerPoints}
          setVoyagePlannerPoints={setVoyagePlannerPoints}
          setPlannerDraftSlot={setPlannerDraftSlot}
          plannerVesselSettings={plannerVesselSettings}
          setPlannerVesselSettings={setPlannerVesselSettings}
          switchVoyagePlannerActive={switchVoyagePlannerActive}
          setSwitchVoyagePlannerActive={setSwitchVoyagePlannerActive}
          voyageActive={voyageActive}
          setVoyageActive={setVoyageActive}
          calculatorBasespeed={calculatorBasespeed}
          setCalculatorBasespeed={setCalculatorBasespeed}
          profileOptions={profileOptions}
          etaResponse={etaResponse}
          setEtaResponse={setEtaResponse}
          routeConsumption={routeConsumption}
          setRouteConsumption={setRouteConsumption}
          cwPortGeojson={cwPortGeojson}
          setCwPortGeojson={setCwPortGeojson}
          startInput={startInput}
          setStartInput={setStartInput}
          startEndpoint={startEndpoint}
          setStartEndpoint={setStartEndpoint}
          startError={startError}
          setStartError={setStartError}
          finishInput={finishInput}
          setFinishInput={setFinishInput}
          finishEndpoint={finishEndpoint}
          setFinishEndpoint={setFinishEndpoint}
          finishError={finishError}
          setFinishError={setFinishError}
          calculatorDepartureTime={calculatorDepartureTime}
          setCalculatorDepartureTime={setCalculatorDepartureTime}
          defaultPlannerVesselSettings={defaultPlannerVesselSettings}
          speedProfileData={speedProfileData}
          setSpeedProfileData={setSpeedProfileData}
          consumptionProfileData={consumptionProfileData}
          setConsumptionProfileData={setConsumptionProfileData}
          cwViaAreasGeojson={cwViaAreasGeojson}
          setToogleButtonsOnOff={setToogleButtonsOnOff}
          switchManualPathing={switchManualPathing}
          plannerRoutingMode={plannerRoutingMode}
          setPlannerRoutingMode={setPlannerRoutingMode}
          pathfinderResponse={pathfinderResponse}
          setPathfinderResponse={setPathfinderResponse}
          etaError={etaError}
          setEtaError={setEtaError}
          applyRnwSafety={applyRnwSafety}
          setApplyRnwSafety={setApplyRnwSafety}
          applyEca={applyEca}
          setApplyEca={setApplyEca}
          avoidHra={avoidHra}
          avoidJwla={avoidJwla}
          avoidCaa={avoidCaa}
          setAvoidHra={setAvoidHra}
          setAvoidJwla={setAvoidJwla}
          setAvoidCaa={setAvoidCaa}
          bunkerPrices={bunkerPrices}
          setBunkerPrices={setBunkerPrices}
          currentDraft={currentDraft}
          setCurrentDraft={setCurrentDraft}
          canSeeErklaerbaer={canSeeErklaerbaer}
          erklaerbaerEnabled={erklaerbaerEnabled}
          setErklaerbaerEnabled={setErklaerbaerEnabled}
          erklaerbaerId={erklaerbaerId}
          setErklaerbaerId={setErklaerbaerId}
          draftPlannerVesselSettings={draftPlannerVesselSettings}
          setDraftPlannerVesselSettings={setDraftPlannerVesselSettings}
          selectedProfile={selectedProfile}
          setSelectedProfile={setSelectedProfile}
          lastPositionByVesselId={lastPositionByVesselId}
          voyageRefreshSignal={voyageRefreshSignal}
          markedVesselNames={markedVesselNames}
          setMarkedVesselNames={setMarkedVesselNames}
          recenterToVesselLastPosition={recenterToVesselLastPosition}
          recenterToPoint={recenterToPoint}
          onGoToVesselPanel={openVesselPanelFromMap}
          newVoyageVesselId={newVoyageVesselId}
        />
        {canSeeRnwAnalyser ? (
          <RnwPanel
            isOpen={openPanels.rnw}
            dispatch={dispatch}
            rnwLegs={rnwLegs}
            setRnwLegs={setRnwLegs}
            cwLegsToViaAreasGeojson={cwLegsToViaAreasGeojson}
            setCwLegsToViaAreasGeojson={setCwLegsToViaAreasGeojson}
            plannerVesselSettings={plannerVesselSettings}
            setPlannerVesselSettings={setPlannerVesselSettings}
            cwViaAreasGeojson={cwViaAreasGeojson}
            voyagePlannerPoints={voyagePlannerPoints}
            calculatorBasespeed={calculatorBasespeed}
            zoomLevel={zoomLevel}
            dragged={dragged}
            resized={resized}
            setVoyageActive={setVoyageActive}
            switchRnwAnalyserActive={switchRnwAnalyserActive}
            setSwitchRnwAnalyserActive={setSwitchRnwAnalyserActive}
            applyRnwSafety={applyRnwSafety}
            setApplyRnwSafety={setApplyRnwSafety}
            defaultSpeedProfileData={defaultSpeedProfileData}
            speedProfileData={speedProfileData}
            consumptionProfileData={consumptionProfileData}
            selectedProfile={selectedProfile}
            markedVesselNames={markedVesselNames}
            setMarkedVesselNames={setMarkedVesselNames}
            defaultPlannerVesselSettings={defaultPlannerVesselSettings}
            draftPlannerVesselSettings={draftPlannerVesselSettings}
            setDraftPlannerVesselSettings={setDraftPlannerVesselSettings}
          />
        ) : null}
        <GisPanel
          openPanels={openPanels}
          dispatch={dispatch}
          setVoyageActive={setVoyageActive}
          switchGisLayersActive={switchGisLayersActive}
          setSwitchGisLayersActive={setSwitchGisLayersActive}
          toogleButtonsOnOff={toogleButtonsOnOff}
          setToogleButtonsOnOff={setToogleButtonsOnOff}
        />
        <MapPanel
          openPanels={openPanels}
          dispatch={dispatch}
          setVoyageActive={setVoyageActive}
          switchMapSettingsActive={switchMapSettingsActive}
          setSwitchMapSettingsActive={setSwitchMapSettingsActive}
          toogleButtonsOnOff={toogleButtonsOnOff}
          setToogleButtonsOnOff={setToogleButtonsOnOff}
        />
        </MapUiSlot>
      </MeteoTrackerPreset>
      <GlobalTimeSliderContainer
        map={map}
        sliderTicks={sliderTicks}
        sliderPosition={sliderPosition}
        setSliderPosition={setSliderPosition}
        shouldDisplayTimeSlider={shouldDisplayTimeSlider}
        setVoyageActive={setVoyageActive}
      />
      <WeatherAvailabilityNotice
        controller={weatherController}
        geoflowSession={geoflowSession}
        timeOutOfRange={weatherTimeOutOfRange}
      />
      <MapAttribution
        map={map}
        show={toogleButtonsOnOff.includes("Attribution")}
      />
    </Box>
  )
}
